import { IconWarning } from "@/assets/icons/geist/IconWarning";
import { Link } from "@/components/flexkit/Link";
import { Label } from "@/components/podkit/forms/Label";
import { cn } from "@/components/podkit/lib/cn";
import {
    SelectContentCombobox,
    SelectItemCombobox,
    SelectTrigger,
    SelectValue,
} from "@/components/podkit/select/Select";
import { Text } from "@/components/podkit/typography/Text";
import {
    getCompareLabel,
    getEnvironmentClassStatus,
    sortEnvironmentClassEntries,
    type EnvironmentClassEntry,
} from "@/hooks/use-environment-class-entries";
import type { PlainRunnerEnvironmentClass } from "@/queries/environment-queries";
import { ComboboxProvider } from "@ariakit/react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { RunnerPhase } from "gitpod-next-api/gitpod/v1/runner_pb";
import { memo, startTransition, useId, useMemo, useState, type FC } from "react";

function selectionFromValue(
    environmentClassEntries: EnvironmentClassEntry[],
    value: string,
): EnvironmentClassEntry | undefined {
    const [runnerId, classId] = value.split(":");
    return environmentClassEntries.find((v) => v?.clazz?.id == classId && v?.clazz.runnerId == runnerId);
}

function valueFromSelection(selection: PlainRunnerEnvironmentClass): string {
    return `${selection.runnerId || "unknown"}:${selection?.id || "unknown"}`;
}

type Props = {
    environmentClassEntries: EnvironmentClassEntry[];
    label: string;
    value?: EnvironmentClassEntry;
    loading: boolean;
    disabled: boolean;
    name: string;
    onChange: (value: EnvironmentClassEntry | undefined) => void;
};

type EnvironmentClassStatusProps = {
    offline: boolean;
    policyRestricted: boolean;
    degraded: boolean;
};

const EnvironmentClassStatus: FC<EnvironmentClassStatusProps> = ({ offline, policyRestricted, degraded }) => {
    if (offline) {
        return <span className="text-sm text-content-tertiary">Offline</span>;
    }
    if (policyRestricted) {
        return <span className="inline-block text-left text-sm text-content-quaternary">Policy restricted</span>;
    }
    if (degraded) {
        return <span className="text-sm text-content-tertiary">Degraded</span>;
    }
    return null;
};

export const EnvironmentClassSelect: FC<Props> = memo(({
    environmentClassEntries,
    label,
    value,
    loading,
    disabled,
    name,
    onChange,
}) => {
    const elId = useId();

    const [open, setOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");

    const hasAvailableClasses = environmentClassEntries.length > 0;
    const isDegraded = value?.runner?.status?.phase === RunnerPhase.DEGRADED;

    const matches = useMemo(() => {
        if (!searchValue) return sortEnvironmentClassEntries(environmentClassEntries);

        const words = searchValue.split(" ");
        const matches = environmentClassEntries.filter((v) => {
            const label = getCompareLabel(v);
            return words.reduce((acc, word) => acc && label.includes(word), true);
        });

        // Radix Select does not work if we don't render the selected item, so we
        // make sure to include it in the list of matches.
        const vv = (value && valueFromSelection(value.clazz)) || "";
        if (value !== undefined) {
            const selectedEnvironmentClass = environmentClassEntries.find((v) => valueFromSelection(v.clazz) == vv);
            if (selectedEnvironmentClass && !matches.includes(selectedEnvironmentClass)) {
                matches.push(selectedEnvironmentClass);
            }
        }
        return sortEnvironmentClassEntries(matches);
    }, [environmentClassEntries, value, searchValue]);

    return (
        <div className="flex items-center space-x-2">
            <div className="flex w-full flex-col gap-2">
                <Label htmlFor={elId} className="font-medium">
                    {label}
                </Label>
                <SelectPrimitive.Root
                    value={value ? valueFromSelection(value.clazz) : undefined}
                    onValueChange={(s) => onChange(selectionFromValue(environmentClassEntries, s))}
                    open={open}
                    name={name}
                    onOpenChange={setOpen}
                    disabled={loading || disabled || !hasAvailableClasses}
                >
                    <ComboboxProvider
                        open={open}
                        setOpen={setOpen}
                        resetValueOnHide
                        includesBaseElement={false}
                        setValue={(value) => {
                            startTransition(() => {
                                setSearchValue(value.toLowerCase());
                            });
                        }}
                    >
                        <SelectTrigger
                            id={elId}
                            data-testid="environment-class-select-trigger"
                            aria-label="Environment class"
                            loading={loading}
                            className="flex flex-row items-center justify-center"
                        >
                            {/* A <span style="pointer-events: none;" /> wraps our value if the item is disabled which breaks out layout
                                As we have no control over the span we instead style it using pseudo selectors ↓ */}
                            <div className="flex h-10 w-11/12 items-center [&>span]:flex [&>span]:w-full">
                                <SelectValue
                                    placeholder={
                                        <span className="flex flex-col justify-center">
                                            {hasAvailableClasses
                                                ? "Select an environment class"
                                                : "No available environment classes"}
                                        </span>
                                    }
                                />
                            </div>
                        </SelectTrigger>

                        {hasAvailableClasses && (
                            <SelectContentCombobox inputPlaceholder="Search environment classes">
                                {matches.map((environmentClassEntry) => {
                                    const { policyRestricted, offline, degraded } =
                                        getEnvironmentClassStatus(environmentClassEntry);
                                    const id = valueFromSelection(environmentClassEntry.clazz);
                                    return (
                                        <SelectItemCombobox
                                            key={id}
                                            value={id}
                                            className={cn(
                                                "group w-full border-b border-border-light px-3 pl-2 pr-8 last:border-b-0",
                                                // Overwriting the default "disabled" behavior as we don't want to apply
                                                // opacity to the borders.
                                                "data-[disabled]:opacity-100",
                                            )}
                                            disabled={offline || policyRestricted}
                                            asChild
                                        >
                                            <div className="flex w-full flex-row items-center justify-between">
                                                <div
                                                    className={cn(
                                                        "flex w-full flex-col items-start",
                                                        (offline || policyRestricted) && "max-w-[85%]",
                                                    )}
                                                >
                                                    <div className="flex w-full gap-1">
                                                        <span
                                                            title={environmentClassEntry?.clazz?.displayName}
                                                            className="overflow-hidden text-ellipsis whitespace-nowrap text-base font-bold text-content-primary group-data-[disabled]:text-content-tertiary"
                                                        >
                                                            {environmentClassEntry?.clazz?.displayName}
                                                        </span>
                                                        <span
                                                            className="overflow-hidden text-ellipsis whitespace-nowrap text-base text-content-tertiary"
                                                            title={environmentClassEntry?.runner?.name}
                                                        >
                                                            {environmentClassEntry?.runner?.name}
                                                        </span>
                                                    </div>
                                                    <div className="flex w-full gap-1">
                                                        <span
                                                            className="overflow-hidden text-ellipsis whitespace-nowrap text-left text-content-tertiary"
                                                            title={environmentClassEntry?.clazz?.description}
                                                        >
                                                            {environmentClassEntry?.clazz?.description}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div>
                                                    <EnvironmentClassStatus
                                                        offline={offline}
                                                        policyRestricted={policyRestricted}
                                                        degraded={degraded}
                                                    />
                                                </div>
                                            </div>
                                        </SelectItemCombobox>
                                    );
                                })}
                            </SelectContentCombobox>
                        )}
                    </ComboboxProvider>
                </SelectPrimitive.Root>
                {isDegraded && (
                    <>
                        <div className="flex flex-col gap-2 rounded-lg border-[0.5px] border-border-base bg-surface-yield p-4">
                            <div className="flex items-center gap-1">
                                <IconWarning size="base" className="text-content-orange" />
                                <Text className="text-sm font-bold">This runner is in a degraded state</Text>
                            </div>
                            <Text className="text-sm">
                                Environments may fail to start successfully on this runner.{" "}
                                <Link to={`/settings/runners/${value?.runner?.runnerId}`} className="underline">
                                    View runner settings for details
                                </Link>
                            </Text>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
});
EnvironmentClassSelect.displayName = "EnvironmentClassSelect";
